import * as THREE from 'three';
import type { Actor } from '../sim/actor';
import { GRADE_RANK } from '../sim/types';

interface Label {
  el: HTMLDivElement;
  name: HTMLDivElement;
  role: HTMLDivElement | null;
  bar: HTMLElement | null;
  quest: HTMLDivElement | null;
  lastHp: number;
  lastQuest: string;
  seen: number;
}

export interface LabelInfo {
  actor: Actor;
  head: THREE.Vector3;
  role?: string;
  questMark?: '!' | '?' | '';
  showBar: boolean;
  title?: string;
}

/**
 * Nameplates, NPC role labels, quest markers and monster HP bars, projected
 * into screen space and pooled per entity.
 */
export class WorldLabels {
  private labels = new Map<number, Label>();
  private tmp = new THREE.Vector3();
  private frame = 0;

  constructor(readonly root: HTMLElement) {}

  private create(info: LabelInfo): Label {
    const a = info.actor;
    const el = document.createElement('div');
    const grade = a.monster?.grade;
    el.className = `wl ${a.kind === 'player' ? 'player' : a.kind === 'npc' ? 'npc' : grade && GRADE_RANK[grade] >= GRADE_RANK.elite ? (GRADE_RANK[grade] >= GRADE_RANK.named ? 'named' : 'elite') : 'mob'}`;
    let quest: HTMLDivElement | null = null;
    if (a.kind === 'npc') {
      quest = document.createElement('div');
      quest.className = 'quest';
      el.appendChild(quest);
    }
    let role: HTMLDivElement | null = null;
    if (info.role) {
      role = document.createElement('div');
      role.className = 'role';
      role.textContent = `[${info.role}]`;
      el.appendChild(role);
    }
    const name = document.createElement('div');
    name.className = 'nm';
    name.textContent = info.title ? `${info.title}` : a.name;
    el.appendChild(name);
    let bar: HTMLElement | null = null;
    if (info.showBar) {
      const b = document.createElement('div');
      b.className = 'bar';
      bar = document.createElement('i');
      b.appendChild(bar);
      el.appendChild(b);
    }
    this.root.appendChild(el);
    return { el, name, role, bar, quest, lastHp: -1, lastQuest: '', seen: 0 };
  }

  update(infos: LabelInfo[], camera: THREE.Camera, w: number, h: number): void {
    this.frame++;
    for (const info of infos) {
      let l = this.labels.get(info.actor.id);
      if (!l) {
        l = this.create(info);
        this.labels.set(info.actor.id, l);
      }
      l.seen = this.frame;
      this.tmp.copy(info.head).project(camera);
      const visible = this.tmp.z < 1 && Math.abs(this.tmp.x) < 1.2 && Math.abs(this.tmp.y) < 1.2;
      if (!visible) {
        l.el.style.display = 'none';
        continue;
      }
      l.el.style.display = '';
      const x = (this.tmp.x * 0.5 + 0.5) * w;
      const y = (-this.tmp.y * 0.5 + 0.5) * h;
      l.el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -100%)`;
      if (l.bar) {
        const hp = info.actor.hpPct;
        if (Math.abs(hp - l.lastHp) > 0.001) {
          l.bar.style.setProperty('--v', hp.toFixed(3));
          l.lastHp = hp;
        }
      }
      if (l.quest && info.questMark !== l.lastQuest) {
        l.quest.textContent = info.questMark ?? '';
        l.lastQuest = info.questMark ?? '';
      }
    }
    for (const [id, l] of this.labels) {
      if (l.seen !== this.frame) {
        l.el.remove();
        this.labels.delete(id);
      }
    }
  }

  clear(): void {
    for (const l of this.labels.values()) l.el.remove();
    this.labels.clear();
  }
}
